import React, { useState, useEffect } from 'react';
import {
  TrendingUp, TrendingDown, ShoppingCart, DollarSign,
  Users, Package, AlertCircle, Clock, CheckCircle, XCircle,
  Activity, Eye, MessageSquare, Star, BarChart3
} from 'lucide-react';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import './AdminDashboard.css';

// Import Firebase services
import productsService from '../../../services/products.service';
import categoriesService from '../../../services/categories.service';
import commentsService from '../../../services/comments.service';
import contactService from '../../../services/contact.service';
import invoicesService from '../../../services/invoices.service';
import visitorsService from '../../../services/visitors.service';
import { db } from '../../../services/firebase/firebaseconfig';
import { collection, getDocs, query, orderBy, limit, where } from 'firebase/firestore';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalOrders: 0,
    revenue: 0,
    customers: 0,
    products: 0,
    pendingOrders: 0,
    completedOrders: 0,
    lowStockProducts: 0,
    newMessages: 0,
    totalVisitors: 0,
    monthlyVisitors: 0
  });

  const [trends, setTrends] = useState({
    ordersChange: 0,
    revenueChange: 0,
    customersChange: 0,
    productsChange: 0,
    visitorsChange: 0
  });

  const [salesData, setSalesData] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  const [visitorsData, setVisitorsData] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Fetch all data in parallel
      const [
        productsResult,
        categoriesResult,
        commentsResult,
        messagesResult,
        invoicesResult,
        ordersSnapshot,
        visitorsResult,
        visitorTrendsResult
      ] = await Promise.all([
        productsService.getAllProducts(),
        categoriesService.getAllCategories(),
        commentsService.getAllComments(),
        contactService.getRecentMessages(100),
        invoicesService.getAllInvoices(),
        getDocs(query(collection(db, 'orders'), orderBy('createdAt', 'desc'), limit(100))),
        visitorsService.getVisitorStats(),
        visitorsService.getVisitorTrends()
      ]);

      // Process orders data
      const orders = ordersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Calculate stats
      const products = productsResult.success ? productsResult.products : [];
      const lowStockProducts = products.filter(p =>
        p.stock === 'out-of-stock' || p.stock === 'low-stock'
      ).length;

      const pendingOrders = orders.filter(o => o.status === 'pending').length;
      const completedOrders = orders.filter(o =>
        o.status === 'delivered' || o.status === 'completed'
      ).length;

      // Calculate revenue from completed orders and invoices
      const ordersRevenue = orders
        .filter(o => o.status === 'delivered' || o.status === 'completed')
        .reduce((sum, o) => sum + (o.total || 0), 0);

      const invoicesRevenue = invoicesResult.success
        ? invoicesResult.invoices
          .filter(i => i.status === 'paid')
          .reduce((sum, i) => sum + (i.total || 0), 0)
        : 0;

      const totalRevenue = ordersRevenue + invoicesRevenue;

      // Get unique customers from orders
      const uniqueCustomers = new Set(orders.map(o => o.email || o.phone)).size;

      // Get unread messages
      const unreadMessages = messagesResult.success
        ? messagesResult.messages.filter(m => !m.read).length
        : 0;

      // Get visitor stats
      const visitorStats = visitorsResult.success ? visitorsResult.stats : {
        totalVisitors: 0,
        monthlyVisitors: 0,
        monthlyData: []
      };

      const visitorTrends = visitorTrendsResult.success ? visitorTrendsResult.trends : {
        change: 0
      };

      // Calculate trends (compare last 7 days vs previous 7 days)
      const now = new Date();
      const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const fourteenDaysAgo = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);

      const recentOrders = orders.filter(o => {
        const orderDate = o.createdAt?.toDate ? o.createdAt.toDate() : new Date(o.createdAt);
        return orderDate >= sevenDaysAgo;
      });

      const previousOrders = orders.filter(o => {
        const orderDate = o.createdAt?.toDate ? o.createdAt.toDate() : new Date(o.createdAt);
        return orderDate >= fourteenDaysAgo && orderDate < sevenDaysAgo;
      });

      const ordersChange = previousOrders.length > 0
        ? ((recentOrders.length - previousOrders.length) / previousOrders.length) * 100
        : 0;

      const recentRevenue = recentOrders.reduce((sum, o) => sum + (o.total || 0), 0);
      const previousRevenue = previousOrders.reduce((sum, o) => sum + (o.total || 0), 0);
      const revenueChange = previousRevenue > 0
        ? ((recentRevenue - previousRevenue) / previousRevenue) * 100
        : 0;

      setStats({
        totalOrders: orders.length,
        revenue: totalRevenue,
        customers: uniqueCustomers,
        products: products.length,
        pendingOrders,
        completedOrders,
        lowStockProducts,
        newMessages: unreadMessages,
        totalVisitors: visitorStats.totalVisitors,
        monthlyVisitors: visitorStats.monthlyVisitors
      });

      setTrends({
        ordersChange: Math.round(ordersChange * 10) / 10,
        revenueChange: Math.round(revenueChange * 10) / 10,
        customersChange: 5.0,
        productsChange: 3.0,
        visitorsChange: visitorTrends.change
      });

      // Set visitors data for chart
      setVisitorsData(visitorStats.monthlyData || []);

      // Generate sales data for last 7 days
      const salesByDay = generateSalesData(orders);
      setSalesData(salesByDay);

      // Generate category distribution
      const categoryCounts = generateCategoryData(products, categoriesResult.categories);
      setCategoryData(categoryCounts);

      // Get recent orders (top 5)
      const formattedRecentOrders = orders.slice(0, 5).map(order => ({
        id: order.orderNumber || order.id.substring(0, 8),
        customer: order.fullName || 'Client anonyme',
        product: order.productName || 'Produit',
        amount: order.total || 0,
        status: order.status || 'pending',
        time: getTimeAgo(order.createdAt)
      }));
      setRecentOrders(formattedRecentOrders);

      // Generate recent activities
      const activities = generateActivities(orders, commentsResult.comments, products, messagesResult.messages, visitorStats);
      setRecentActivities(activities);

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to generate sales data for last 7 days
  const generateSalesData = (orders) => {
    const days = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
    const salesByDay = [];

    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      date.setHours(0, 0, 0, 0);

      const nextDate = new Date(date);
      nextDate.setDate(nextDate.getDate() + 1);

      const dayOrders = orders.filter(order => {
        const orderDate = order.createdAt?.toDate ? order.createdAt.toDate() : new Date(order.createdAt);
        return orderDate >= date && orderDate < nextDate;
      });

      const dayRevenue = dayOrders.reduce((sum, order) => sum + (order.total || 0), 0);

      salesByDay.push({
        date: days[date.getDay()],
        orders: dayOrders.length,
        revenue: dayRevenue
      });
    }

    return salesByDay;
  };

  // Helper function to generate category distribution
  const generateCategoryData = (products, categories) => {
    const colors = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4'];
    const categoryCounts = {};

    products.forEach(product => {
      const category = product.category || 'Autres';
      categoryCounts[category] = (categoryCounts[category] || 0) + 1;
    });

    return Object.entries(categoryCounts)
      .map(([name, value], index) => ({
        name,
        value,
        color: colors[index % colors.length]
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 6);
  };

  // Helper function to generate recent activities
  const generateActivities = (orders, comments, products, messages, visitorStats) => {
    const activities = [];

    // Recent visitors activity
    if (visitorStats.monthlyVisitors > 0) {
      activities.push({
        type: 'visitors',
        message: `${visitorStats.monthlyVisitors} visiteurs ce mois`,
        time: 'Ce mois',
        icon: Eye,
        color: '#8b5cf6'
      });
    }

    // Recent orders
    orders.slice(0, 2).forEach(order => {
      activities.push({
        type: 'order',
        message: `Nouvelle commande #${order.orderNumber || order.id.substring(0, 8)}`,
        time: getTimeAgo(order.createdAt),
        icon: ShoppingCart,
        color: '#3b82f6'
      });
    });

    // Recent comments
    if (comments && comments.length > 0) {
      const recentComment = comments[0];
      activities.push({
        type: 'review',
        message: `Nouveau commentaire de ${recentComment.author}`,
        time: getTimeAgo(recentComment.createdAt),
        icon: MessageSquare,
        color: '#10b981'
      });
    }

    // Low stock alerts
    const lowStockProducts = products.filter(p => p.stock === 'low-stock');
    if (lowStockProducts.length > 0) {
      activities.push({
        type: 'stock',
        message: `Alerte stock bas: ${lowStockProducts[0].name}`,
        time: 'Maintenant',
        icon: AlertCircle,
        color: '#f59e0b'
      });
    }

    // Recent messages
    if (messages && messages.length > 0) {
      const recentMessage = messages[0];
      activities.push({
        type: 'message',
        message: `Nouveau message de ${recentMessage.name}`,
        time: getTimeAgo(recentMessage.createdAt),
        icon: MessageSquare,
        color: '#8b5cf6'
      });
    }

    return activities.slice(0, 6);
  };

  // Helper function to calculate time ago
  const getTimeAgo = (timestamp) => {
    if (!timestamp) return 'Récemment';

    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'À l\'instant';
    if (diffMins < 60) return `${diffMins} min`;
    if (diffHours < 24) return `${diffHours}h`;
    return `${diffDays}j`;
  };

  const CircularProgress = ({ percentage, label, color }) => {
    const radius = 45;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference - (percentage / 100) * circumference;

    return (
      <div className="admin-dashboard__circular-progress">
        <svg width="120" height="120" style={{ transform: 'rotate(-90deg)' }}>
          <circle
            cx="60"
            cy="60"
            r={radius}
            fill="none"
            stroke="var(--color-bg-tertiary)"
            strokeWidth="10"
          />
          <circle
            cx="60"
            cy="60"
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth="10"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            style={{ transition: 'stroke-dashoffset 1s ease' }}
          />
          <text
            x="60"
            y="60"
            textAnchor="middle"
            dominantBaseline="middle"
            style={{
              transform: 'rotate(90deg)',
              transformOrigin: '60px 60px',
              fontSize: '20px',
              fontWeight: 'bold',
              fill: 'var(--color-text-primary)'
            }}
          >
            {percentage}%
          </text>
        </svg>
        <span className="admin-dashboard__circular-progress-label">
          {label}
        </span>
      </div>
    );
  };

  const StatCard = ({ icon: Icon, title, value, change, color }) => (
    <div className="admin-dashboard__stat-card">
      <div className="admin-dashboard__stat-card-header">
        <div
          className="admin-dashboard__stat-icon"
          style={{ background: `${color}20` }}
        >
          <Icon size={24} color={color} />
        </div>
        {change !== undefined && (
          <div className={`admin-dashboard__stat-change ${change >= 0 ? 'admin-dashboard__stat-change--positive' : 'admin-dashboard__stat-change--negative'}`}>
            {change >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
            {Math.abs(change)}%
          </div>
        )}
      </div>
      <h3 className="admin-dashboard__stat-value">
        {typeof value === 'number' && value > 1000 ? value.toLocaleString() : value}
      </h3>
      <p className="admin-dashboard__stat-title">{title}</p>
    </div>
  );

  if (loading) {
    return (
      <div className="admin-dashboard__loading">
        <div className="admin-dashboard__spinner" />
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      {/* Stats Grid */}
      <div className="admin-dashboard__stats-grid">
        <StatCard
          icon={Eye}
          title="Visiteurs Totaux"
          value={stats.totalVisitors}
          change={trends.visitorsChange}
          color="#8b5cf6"
        />
        <StatCard
          icon={Users}
          title="Visiteurs Ce Mois"
          value={stats.monthlyVisitors}
          color="#6366f1"
        />
        <StatCard
          icon={ShoppingCart}
          title="Commandes Totales"
          value={stats.totalOrders}
          change={trends.ordersChange}
          color="#3b82f6"
        />
        <StatCard
          icon={DollarSign}
          title="Revenus"
          value={`${(stats.revenue).toFixed(1)} DA`}
          change={trends.revenueChange}
          color="#10b981"
        />
        <StatCard
          icon={Users}
          title="Clients"
          value={stats.customers}
          change={trends.customersChange}
          color="#f59e0b"
        />
        <StatCard
          icon={Package}
          title="Produits"
          value={stats.products}
          change={trends.productsChange}
          color="#ef4444"
        />
      </div>

      {/* Charts Row */}
      <div className="admin-dashboard__charts-row">
        {/* Visitors Chart */}
        <div className="admin-dashboard__chart-card">
          <h3 className="admin-dashboard__chart-title">
            Évolution des Visiteurs (12 derniers mois)
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={visitorsData}>
              <defs>
                <linearGradient id="colorVisitors" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-light)" />
              <XAxis dataKey="month" stroke="var(--color-text-secondary)" />
              <YAxis stroke="var(--color-text-secondary)" />
              <Tooltip
                contentStyle={{
                  background: 'var(--color-bg-elevated)',
                  border: '1px solid var(--color-border-primary)',
                  borderRadius: '8px',
                  fontSize: '15px'
                }}
              />
              <Area
                type="monotone"
                dataKey="visitors"
                stroke="#8b5cf6"
                fillOpacity={1}
                fill="url(#colorVisitors)"
                strokeWidth={2}
                name="Visiteurs"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Sales Chart */}
        <div className="admin-dashboard__chart-card">
          <h3 className="admin-dashboard__chart-title">
            Ventes des 7 derniers jours
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={salesData}>
              <defs>
                <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-light)" />
              <XAxis dataKey="date" stroke="var(--color-text-secondary)" />
              <YAxis stroke="var(--color-text-secondary)" />
              <Tooltip
                contentStyle={{
                  background: 'var(--color-bg-elevated)',
                  border: '1px solid var(--color-border-primary)',
                  borderRadius: '8px',
                  fontSize: '15px'
                }}
              />
              <Area
                type="monotone"
                dataKey="revenue"
                stroke="#3b82f6"
                fillOpacity={1}
                fill="url(#colorRevenue)"
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Category Distribution Chart */}
      <div className="admin-dashboard__charts-row">
        <div className="admin-dashboard__chart-card">
          <h3 className="admin-dashboard__chart-title">
            Produits par Catégorie
          </h3>
          {categoryData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={categoryData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  style={{ fontSize: "14px" }}
                >
                  {categoryData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="admin-dashboard__empty-state">
              <p>Aucune donnée disponible</p>
            </div>
          )}
        </div>

        {/* Circular Progress Stats */}
        <div className="admin-dashboard__chart-card">
          <h3 className="admin-dashboard__chart-title">
            Performance Overview
          </h3>
          <div className="admin-dashboard__progress-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
            <CircularProgress
              percentage={stats.totalOrders > 0 ? Math.round((stats.completedOrders / stats.totalOrders) * 100) : 0}
              label="Commandes Complétées"
              color="#10b981"
            />
            <CircularProgress
              percentage={stats.products > 0 ? Math.round(((stats.products - stats.lowStockProducts) / stats.products) * 100) : 100}
              label="Stock Disponible"
              color="#3b82f6"
            />
          </div>
        </div>
      </div>

      {/* Bottom Row: Recent Orders & Activities */}
      <div className="admin-dashboard__bottom-row">
        {/* Recent Orders */}
        <div className="admin-dashboard__card">
          <div className="admin-dashboard__card-header">
            <h3 className="admin-dashboard__card-title">
              Commandes Récentes
            </h3>
          </div>
          <div className="admin-dashboard__orders-list">
            {recentOrders.length > 0 ? (
              recentOrders.map(order => (
                <div key={order.id} className="admin-dashboard__order-item">
                  <div className="admin-dashboard__order-info">
                    <div className="admin-dashboard__order-id">
                      #{order.id} - {order.customer}
                    </div>
                    <div className="admin-dashboard__order-product">
                      {order.product}
                    </div>
                  </div>
                  <div className="admin-dashboard__order-details">
                    <div className="admin-dashboard__order-amount">
                      {order.amount.toLocaleString()} DA
                    </div>
                    <span className={`admin-dashboard__order-status admin-dashboard__order-status--${order.status}`}>
                      {order.status === 'completed' || order.status === 'delivered' ? 'Complétée' :
                        order.status === 'pending' ? 'En attente' :
                          order.status === 'processing' ? 'Traitement' : order.status}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="admin-dashboard__empty-state">
                <p>Aucune commande récente</p>
              </div>
            )}
          </div>
        </div>

        {/* Recent Activities */}
        <div className="admin-dashboard__card">
          <h3 className="admin-dashboard__card-title">
            Activités Récentes
          </h3>
          <div className="admin-dashboard__activities-list">
            {recentActivities.length > 0 ? (
              recentActivities.map((activity, index) => {
                const Icon = activity.icon;
                return (
                  <div key={index} className="admin-dashboard__activity-item">
                    <div
                      className="admin-dashboard__activity-icon"
                      style={{ background: `${activity.color}20` }}
                    >
                      <Icon size={20} color={activity.color} />
                    </div>
                    <div className="admin-dashboard__activity-content">
                      <div className="admin-dashboard__activity-message">
                        {activity.message}
                      </div>
                      <div className="admin-dashboard__activity-time">
                        Il y a {activity.time}
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="admin-dashboard__empty-state">
                <p>Aucune activité récente</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;